import { useEffect, useRef, useState } from "react";

import styles from "./SavedOrdersControls.module.sass";

const SAVED_ORDERS_STORAGE_KEY = "moderators-heatmap:saved-orders";

const loadSavedOrders = () => {
  try {
    const raw = localStorage.getItem(SAVED_ORDERS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item) =>
        item &&
        typeof item.id === "string" &&
        typeof item.name === "string" &&
        Array.isArray(item.order) &&
        item.order.every((uuid) => typeof uuid === "string")
    );
  } catch {
    return [];
  }
};

const persistSavedOrders = (orders) => {
  try {
    localStorage.setItem(SAVED_ORDERS_STORAGE_KEY, JSON.stringify(orders));
  } catch {
    // ignore quota / disabled storage
  }
};

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const SavedOrdersControls = ({
  currentOrder,
  onApplyOrder,
  onResetOrder,
  translate,
}) => {
  const [savedOrders, setSavedOrders] = useState(loadSavedOrders);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [duplicateError, setDuplicateError] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const onMouseDown = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setDropdownOpen(false);
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (!modalOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setModalOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [modalOpen]);

  const openModal = () => {
    setNameInput("");
    setDuplicateError(false);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleNameChange = (event) => {
    setNameInput(event.target.value);
    if (duplicateError) setDuplicateError(false);
  };

  const handleSave = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    if (savedOrders.some((entry) => entry.name === trimmed)) {
      setDuplicateError(true);
      return;
    }
    const next = [
      ...savedOrders,
      { id: generateId(), name: trimmed, order: currentOrder },
    ];
    persistSavedOrders(next);
    setSavedOrders(next);
    closeModal();
  };

  const handleApply = (order) => {
    onApplyOrder(order);
    setDropdownOpen(false);
  };

  const handleDelete = (event, entry) => {
    event.stopPropagation();
    const confirmed = window.confirm(
      translate("delete_order_confirm", { name: entry.name })
    );
    if (!confirmed) return;
    const next = savedOrders.filter((item) => item.id !== entry.id);
    persistSavedOrders(next);
    setSavedOrders(next);
  };

  const hasCustomOrder = currentOrder && currentOrder.length > 0;
  const canSave = hasCustomOrder;
  const canSubmit = nameInput.trim().length > 0;

  return (
    <div className={styles.controls}>
      <div className={styles.dropdown} ref={dropdownRef}>
        <button
          type="button"
          className={styles.button}
          onClick={() => setDropdownOpen((value) => !value)}
        >
          {translate("saved_orders")} ▾
        </button>
        {dropdownOpen && (
          <div className={styles.menu}>
            {savedOrders.length === 0 ? (
              <div className={styles.empty}>
                {translate("no_saved_orders")}
              </div>
            ) : (
              savedOrders.map((entry) => (
                <div key={entry.id} className={styles.item}>
                  <button
                    type="button"
                    className={styles.itemName}
                    onClick={() => handleApply(entry.order)}
                  >
                    {entry.name}
                  </button>
                  <button
                    type="button"
                    className={styles.itemDelete}
                    aria-label="Delete"
                    onClick={(event) => handleDelete(event, entry)}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.button}
        onClick={openModal}
        disabled={!canSave}
      >
        {translate("save_order")}
      </button>

      {hasCustomOrder && (
        <button
          type="button"
          className={styles.button}
          onClick={onResetOrder}
        >
          {translate("reset_order")}
        </button>
      )}

      {modalOpen && (
        <div className={styles.overlay} onClick={closeModal}>
          <div
            className={styles.modal}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className={styles.modalTitle}>
              {translate("save_order_modal_title")}
            </h3>
            <input
              type="text"
              className={styles.input}
              placeholder={translate("save_order_name_placeholder")}
              value={nameInput}
              onChange={handleNameChange}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleSave();
              }}
              autoFocus
            />
            {duplicateError && (
              <div className={styles.error}>
                {translate("save_order_duplicate_error")}
              </div>
            )}
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.button}
                onClick={closeModal}
              >
                {translate("cancel")}
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={handleSave}
                disabled={!canSubmit}
              >
                {translate("save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedOrdersControls;
